<template>
  <div class="approval-actions-container">
    <div
      v-if="isLoading"
      class="approval-actions-loading"
    >
      <ApprovalActionsSkeletonLoader />
    </div>

    <template v-else-if="flow && profile">
      <section class="left-column job-profile-content">
        <h1 v-if="profile.title" class="text-h1 text-textDarkest mb-4">
          {{ profile.title }}
        </h1>
        <dl v-if="metadataRows.length" class="profile-metadata">
          <template
            v-for="row in metadataRows"
            :key="row.label"
          >
            <dt class="text-body-2 text-textLight">
              {{ row.label }}
            </dt>
            <dd class="text-body-2 text-textDarkest">
              {{ row.value }}
            </dd>
          </template>
        </dl>

        <div
          v-if="!displayFields.length"
          class="approval-content-empty text-body-2 text-textLight"
          :class="{ 'approval-content-empty-only': !hasProfileHeaderContent }"
        >
          This job profile has no content to review.
        </div>

        <section
          v-for="field in displayFields"
          :key="field.id"
          class="profile-field"
        >
          <h2
            v-if="field.label"
            class="text-h2 text-textDarkest mb-2"
          >
            {{ field.label }}
          </h2>
          <DocumentPreviewFieldHtml
            v-if="field.headerHtml && !isEmptyHtml(field.headerHtml)"
            :html="field.headerHtml"
          />
          <DocumentPreviewFieldHtml
            v-if="field.html && !isEmptyHtml(field.html)"
            :html="field.html"
          />
          <DocumentPreviewFieldHtml
            v-if="field.footerHtml && !isEmptyHtml(field.footerHtml)"
            :html="field.footerHtml"
          />
        </section>
      </section>

      <section class="right-column review-panel">
        <div class="decision-step">
          <h2 class="panel-title">
            {{ panelText.title }}
          </h2>
          <p class="panel-instruction">
            {{ panelText.instruction }}
          </p>

          <div v-if="approvalStep === 'review'" class="two-buttons">
            <DpplDarkTooltip
              light-mode
              tooltip-placement="top"
              :hide-tooltip="!disabledReason"
              class="approval-action-tooltip"
            >
              <template #activator="{ onMouseenter, onMouseleave }">
                <span
                  class="tooltip-button-wrapper"
                  @mouseenter="onMouseenter"
                  @mouseleave="onMouseleave"
                >
                  <DpplNewButton
                    class="green-button thin-button"
                    :disabled="areActionsDisabled"
                    :loading="isSubmitting"
                    @click="handleApprove"
                  >
                    Approve
                  </DpplNewButton>
                </span>
              </template>
              <template #tooltip-content>
                <div class="text-body-2 text-textDark">
                  {{ disabledReason }}
                </div>
              </template>
            </DpplDarkTooltip>
            <DpplDarkTooltip
              light-mode
              tooltip-placement="top"
              :hide-tooltip="!disabledReason"
              class="approval-action-tooltip"
            >
              <template #activator="{ onMouseenter, onMouseleave }">
                <span
                  class="tooltip-button-wrapper"
                  @mouseenter="onMouseenter"
                  @mouseleave="onMouseleave"
                >
                  <DpplNewButton
                    class="red-button thin-button"
                    :disabled="areActionsDisabled"
                    @click="handleRejectClicked"
                  >
                    Reject
                  </DpplNewButton>
                </span>
              </template>
              <template #tooltip-content>
                <div class="text-body-2 text-textDark">
                  {{ disabledReason }}
                </div>
              </template>
            </DpplDarkTooltip>
          </div>
        </div>

        <div
          v-if="approvalStep === 'rejectConfirmation'"
          class="two-buttons mt-4"
        >
          <DpplNewButton
            class="outline-grey thin-button"
            :disabled="isSubmitting"
            @click="handleRejectCancelled"
          >
            Cancel
          </DpplNewButton>
          <DpplNewButton
            class="red-button thin-button"
            :disabled="isSubmitting || !pendingSlotUlid"
            :loading="isSubmitting"
            @click="handleRejectConfirmed"
          >
            Confirm Rejection
          </DpplNewButton>
        </div>

        <DpplNewButton
          v-if="approvalStep === 'approvalSubmitted' || approvalStep === 'approvalRejected'"
          class="view-other-pending-buttons outline-grey d-flex justify-center align-center w-100"
        >
          <router-link :to="{ name: 'home' }">
            View Other Pending Approvals
          </router-link>
        </DpplNewButton>
      </section>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import DpplDarkTooltip from '@/components/datapeople/DpplDarkTooltip.vue';
import DocumentPreviewFieldHtml from '@ui/components/general/DocumentPreviewFieldHtml/DocumentPreviewFieldHtml.vue';
import {
  fetchApprovalFlow,
  fetchApprovalFlowRevision,
  approveApprovalSlot,
  rejectApprovalSlot,
} from '@/api/approvalFlows';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { formatUserDisplayName } from '@/helpers/user';
import { formatJobLevel } from '@/helpers/jobLevelDisplay';
import { isEmptyHtml } from '@ui/helpers/htmlToText';
import ApprovalActionsSkeletonLoader from '@/components/shared/SkeletonLoaders/ApprovalActionsSkeletonLoader.vue';
import { useTracking } from '@/stores/tracking/tracking';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
});

const { authData } = storeToRefs(useAppAuthData());
const { addErrorSnackbar } = useAppSnackbar();

const flow = ref(null);
const profile = ref(null);
const isLoading = ref(false);
const isSubmitting = ref(false);
const approvalStep = ref('review');

const createdByName = computed(() => (
  formatUserDisplayName(flow.value?.created_by) || 'A team member'
));

const metadataRows = computed(() => {
  const draftProfile = profile.value || {};

  return [
    { label: 'Job Code', value: draftProfile.job_code },
    { label: 'Job Level', value: formatJobLevel(draftProfile.job_level, { mode: 'long' }) },
    { label: 'Department', value: draftProfile.department },
    { label: 'Location', value: draftProfile.location },
  ].filter((row) => row.value);
});

const hasProfileHeaderContent = computed(() => (
  Boolean(profile.value?.title) || metadataRows.value.length > 0
));

const displayFields = computed(() => {
  const fields = profile.value?.fields || [];

  return fields
    .map((field) => {
      const blocks = typeof field.blocks === 'string'
        ? [{ html: field.blocks }]
        : (field.blocks || []);

      const headerBlocks = Array.isArray(field.header) ? field.header : [];
      const footerBlocks = Array.isArray(field.footer) ? field.footer : [];

      return {
        ...field,
        id: field.id || field.type,
        html: blocks.map((block) => block.html || '').join(''),
        headerHtml: headerBlocks.map((block) => block.html || '').join(''),
        footerHtml: footerBlocks.map((block) => block.html || '').join(''),
      };
    })
    .filter((field) => (
      !isEmptyHtml(field.html)
      || !isEmptyHtml(field.headerHtml)
      || !isEmptyHtml(field.footerHtml)
    ));
});

const currentStageSlots = computed(() => {
  const stages = flow.value?.stages || [];
  const currentStageNumber = flow.value?.current_stage;

  return stages.find((stage) => stage.stage_number === currentStageNumber)?.slots || [];
});

const isCurrentUserApprover = (approver) => {
  const email = authData.value.email?.toLowerCase();

  if (!email) return false;

  return approver.email?.toLowerCase() === email;
};

// userSlots are the slots that the current user is an approver for
const userSlots = computed(() => (
  flow.value?.stages?.flatMap((stage) => stage.slots || []) || []
).filter((slot) => slot.approvers?.some(isCurrentUserApprover)));

// pendingSlotUlid is the ulid of the pending slot that the current user is an approver for
const pendingSlotUlid = computed(() => (
  currentStageSlots.value.find(
    (slot) => slot.status === 'pending'
      && slot.approvers?.some(isCurrentUserApprover),
  )?.ulid
));

const mySlot = computed(() => {
  const currentStageMatch = currentStageSlots.value.find(
    (slot) => slot.approvers?.some(isCurrentUserApprover),
  );

  if (currentStageMatch) return currentStageMatch;

  const pendingUserSlot = userSlots.value.find((slot) => slot.status === 'pending');

  if (pendingUserSlot) return pendingUserSlot;

  return userSlots.value[0] || null;
});

const mySlotStage = computed(() => {
  if (!mySlot.value) return null;

  return flow.value?.stages?.find(
    (stage) => stage.slots?.some((slot) => slot.ulid === mySlot.value.ulid),
  ) || null;
});

const disabledReason = computed(() => {
  const flowStatus = flow.value?.status?.toLowerCase();

  if (flowStatus === 'cancelled') {
    return 'This approval workflow has been cancelled.';
  }

  if (flowStatus === 'rejected') {
    return 'This approval workflow has been rejected.';
  }

  if (flowStatus === 'approved') {
    return 'This approval workflow has already been fully approved.';
  }

  const slot = mySlot.value;

  if (!slot) return null;

  const slotStatus = slot.status?.toLowerCase();

  if (slotStatus === 'approved') {
    return 'This slot has already been approved.';
  }

  if (slotStatus === 'rejected') {
    return 'This slot has already been rejected.';
  }

  const currentStageNumber = flow.value?.current_stage;
  const slotStageNumber = mySlotStage.value?.stage_number;

  if (
    slotStageNumber != null
    && currentStageNumber != null
    && slotStageNumber !== currentStageNumber
  ) {
    if (slotStageNumber < currentStageNumber) {
      return 'This stage has already passed.';
    }

    const currentStage = flow.value?.stages?.find(
      (stage) => stage.stage_number === currentStageNumber,
    );
    const stageName = currentStage?.name;
    const stageLabel = stageName
      ? `Stage ${currentStageNumber} — ${stageName}`
      : `Stage ${currentStageNumber}`;

    return `This approval is currently on ${stageLabel}. You'll be able to act once it reaches your stage.`;
  }

  return null;
});

const areActionsDisabled = computed(() => (
  isSubmitting.value || Boolean(disabledReason.value) || !pendingSlotUlid.value
));

const panelText = computed(() => ({
  review: {
    title: 'Your Review',
    instruction: `${createdByName.value} has invited you to approve this role.`,
  },
  approvalSubmitted: {
    title: 'Approval Submitted',
    instruction: 'Your approval has been recorded. The next stage is now underway.',
  },
  rejectConfirmation: {
    title: 'Reject this approval?',
    instruction: 'Rejecting it will cancel the entire approval workflow.',
  },
  approvalRejected: {
    title: 'Approval Rejected',
    instruction: `This approval workflow has been cancelled. ${createdByName.value} has been notified.`,
  },
}[approvalStep.value || 'review']));

const setInitialApprovalStep = () => {
  if (flow.value?.status === 'rejected') {
    approvalStep.value = 'approvalRejected';
    return;
  }

  const slot = mySlot.value;

  if (slot?.status === 'rejected') {
    approvalStep.value = 'approvalRejected';
    return;
  }

  if (flow.value?.status === 'approved') {
    approvalStep.value = 'approvalSubmitted';
    return;
  }

  if (pendingSlotUlid.value) {
    approvalStep.value = 'review';
    return;
  }

  if (slot?.status === 'approved') {
    approvalStep.value = 'approvalSubmitted';
    return;
  }

  approvalStep.value = 'review';
};

const loadApprovalData = async () => {
  isLoading.value = true;
  flow.value = null;
  profile.value = null;

  try {
    const flowData = await fetchApprovalFlow({ flowUlid: props.id });

    flow.value = flowData;

    if (!flowData?.draft_job_profile_ulid) {
      throw new Error('Approval flow is missing a draft job profile');
    }

    if (!flowData?.draft_revision_ulid) {
      throw new Error('Approval flow is missing a draft revision');
    }

    const revisionData = await fetchApprovalFlowRevision({
      revisionUlid: flowData.draft_revision_ulid,
    });

    profile.value = revisionData.profile;

    setInitialApprovalStep();
  } catch (error) {
    addErrorSnackbar({
      text: `Failed to load approval — ${error.message}`,
    });

    throw error;
  } finally {
    isLoading.value = false;
  }
};

const handleApprove = async () => {
  if (!pendingSlotUlid.value) return;

  isSubmitting.value = true;

  try {
    await approveApprovalSlot({ approvalFlowSlotUlid: pendingSlotUlid.value });
    useTracking().approvalActionsApproved();

    approvalStep.value = 'approvalSubmitted';
  } catch (error) {
    addErrorSnackbar({
      text: `Failed to approve — ${error.message}`,
    });

    throw error;
  } finally {
    isSubmitting.value = false;
  }
};

const handleRejectClicked = () => {
  approvalStep.value = 'rejectConfirmation';
};

const handleRejectConfirmed = async () => {
  if (!pendingSlotUlid.value) return;

  isSubmitting.value = true;

  try {
    await rejectApprovalSlot({ approvalFlowSlotUlid: pendingSlotUlid.value });

    useTracking().approvalActionsRejected();

    approvalStep.value = 'approvalRejected';
  } catch (error) {
    addErrorSnackbar({
      text: `Failed to reject approval — ${error.message}`,
    });

    throw error;
  } finally {
    isSubmitting.value = false;
  }
};

const handleRejectCancelled = () => {
  approvalStep.value = 'review';
};

onMounted(() => {
  useTracking().trackPageLoad({ page: 'Approval Actions: Open' });

  loadApprovalData();
});
</script>

<style lang="less" scoped>
.approval-actions-container {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 400px;
  height: 100%;
  background-color: @bg-default;
}

.approval-actions-loading {
  grid-column: 1 / -1;
  height: 100%;
}

.left-column {
  padding: 36px 150px 36px 60px;
  overflow-y: auto;
  border-right: 1px solid @stroke-default;
}

.right-column {
  padding: 36px;
  overflow-y: auto;
}

.profile-metadata {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 8px;
  margin: 0 0 16px;
}

.approval-content-empty {
  padding: 16px 0;
}

.approval-content-empty-only {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  text-align: center;
  padding: 24px;
}

.profile-field {
  margin-bottom: 16px;
}

.job-profile-content {
  text-align: left;
}

.decision-step {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.panel-title {
  font-size: 20px;
  line-height: 1.5;
  font-weight: 600;
  margin: 0;
  color: @text-darkest;
}

.panel-instruction {
  font-size: 13px;
  line-height: 1.5;
  font-weight: 500;
  margin: 0;
  color: @text-dark;
}

.two-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  :deep(.dppl-button) {
    width: 100%;
  }
}

.approval-action-tooltip {
  width: 100%;
}

.tooltip-button-wrapper {
  display: block;
  width: 100%;
}

.thin-button {
  padding: 8px !important;
}

.green-button {
  background-color: @teal-500;
  color: @text-white;

  &:hover {
    opacity: 0.7;
  }
}

.red-button {
  background-color: @red-700;
  color: @text-white;

  &:hover {
    opacity: 0.7;
  }
}

.outline-grey {
  border: 1px solid @stroke-default;
  color: @text-darkest;
  border-radius: 8px;

  a {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 8px;
    color: @text-darkest;
    font-size: 12px;
    font-weight: 500;
    text-decoration: none;
    width: 100%;
  }
}

.view-other-pending-buttons {
  margin-top: 16px;
}
</style>
